"use client";

import { useServer } from "@/components/providers/ServerProvider";
import StatCard from "@/components/ui/StatCard";

import ServerControls from "./ServerControls";
import ServerConsole from "./ServerConsole";
import { formatUptime } from "./formatUptime";

function formatMemory(
  bytes: number | null
) {
  if (bytes === null) {
    return "—";
  }

  const gb =
    bytes /
    1024 /
    1024 /
    1024;

  return `${gb.toFixed(2)} GB`;
}

export default function MinecraftDashboard() {
  const {
    state,
    uptime,
    cpu,
    memory,
    players,
    playerCount,
    maxPlayers,
    loading,
    error,
  } = useServer();

  const statusConfig = {
    offline: {
      label: "Offline",
      dot: "bg-red-500",
      text: "text-red-400",
    },

    starting: {
      label: "Starting...",
      dot: "bg-yellow-400 animate-pulse",
      text: "text-yellow-400",
    },

    online: {
      label: "Online",
      dot: "bg-green-400",
      text: "text-green-400",
    },

    stopping: {
      label: "Stopping...",
      dot: "bg-yellow-400 animate-pulse",
      text: "text-yellow-400",
    },

    error: {
      label: "Error",
      dot: "bg-red-500 animate-pulse",
      text: "text-red-400",
    },
  };

  const currentStatus =
    statusConfig[state];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Status */}

        <div
          className={`flex items-center gap-2 ${currentStatus.text}`}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full ${currentStatus.dot}`}
          />

          <span className="font-medium">
            {loading
              ? "Checking..."
              : currentStatus.label}
          </span>
        </div>

      </div>

      {/* Error */}

      {error && (
        <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Stats */}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8">

        <StatCard
          title="PLAYERS"
          value={`${playerCount} / ${maxPlayers}`}
        />

        <StatCard
          title="CPU"
          value={
            cpu !== null
              ? `${cpu.toFixed(1)}%`
              : "—"
          }
        />

        <StatCard
          title="RAM"
          value={formatMemory(
            memory
          )}
        />

        <StatCard
          title="UPTIME"
          value={formatUptime(
            uptime
          )}
        />

      </div>

      <ServerControls />

      <ServerConsole />

      {/* Player list */}

      {playerCount > 0 && players.length > 0 && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#181b1f] overflow-hidden">

          <div className="px-4 sm:px-5 py-4 border-b border-white/10 flex items-center justify-between">

            <div>
              <h2 className="font-medium">
                Players
              </h2>

              <p className="text-xs text-gray-500 mt-1">
                Currently connected
              </p>
            </div>

            <span className="text-sm text-gray-400">
              {playerCount} online
            </span>

          </div>

          <div className="divide-y divide-white/5">

            {players.map(
              (player) => (
                <div
                  key={player}
                  className="px-4 sm:px-5 py-3 flex items-center gap-3"
                >
                  <span className="h-2 w-2 rounded-full bg-green-400" />

                  <span className="text-sm">
                    {player}
                  </span>

                  <span className="ml-auto text-xs text-green-400">
                    ONLINE
                  </span>
                </div>
              )
            )}

          </div>

        </div>
      )}

    </div>
  );
}